import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, MapPin, Users, Camera, Wifi, WifiOff, DollarSign,
  Car, Globe, Plus, Edit, Power, Shield, ChevronRight, BarChart3, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";

export default function SuperAdminPage() {
  const { user, checkAuth } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: '', business_name: '', tax_id: '', plan: 'free' });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgSites, setOrgSites] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  const fetchData = async () => {
    try {
      const [m, o] = await Promise.all([
        axios.get(`${API}/admin/metrics`, { withCredentials: true }),
        axios.get(`${API}/admin/organizations`, { withCredentials: true })
      ]);
      setMetrics(m.data);
      setOrgs(o.data);
    } catch {}
  };

  useEffect(() => { fetchData(); }, []);

  const handleSaveOrg = async () => {
    try {
      if (editingOrg) {
        await axios.put(`${API}/admin/organizations/${editingOrg.id}`, orgForm, { withCredentials: true });
        toast.success('Organizacion actualizada');
      } else {
        await axios.post(`${API}/admin/organizations`, orgForm, { withCredentials: true });
        toast.success('Organizacion creada');
      }
      setShowOrgModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const toggleOrgStatus = async (orgId) => {
    try {
      const { data } = await axios.put(`${API}/admin/organizations/${orgId}/status`, {}, { withCredentials: true });
      toast.success(`Estado: ${data.status}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const navigate = useNavigate();
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  const openImpersonateModal = (org, e) => {
    if (e) e.stopPropagation();
    setImpersonateTarget(org);
    setImpersonateReason('');
    setShowImpersonateModal(true);
  };

  const confirmImpersonate = async () => {
    if (!impersonateTarget) return;
    if (!impersonateReason.trim() || impersonateReason.trim().length < 3) {
      toast.error('Ingresa un motivo (minimo 3 caracteres)');
      return;
    }
    setImpersonating(true);
    try {
      const { data } = await axios.post(`${API}/admin/impersonate/${impersonateTarget.id}`,
        { reason: impersonateReason.trim() }, { withCredentials: true });
      toast.success(`Impersonando a ${data.target_email}`);
      setShowImpersonateModal(false);
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al impersonar');
    } finally {
      setImpersonating(false);
    }
  };


  const openOrgDetail = async (org) => {
    setSelectedOrg(org);
    try {
      const [sites, users] = await Promise.all([
        axios.get(`${API}/admin/sites?organization_id=${org.id}`, { withCredentials: true }),
        axios.get(`${API}/auth/users`, { withCredentials: true })
      ]);
      setOrgSites(sites.data);
      setOrgUsers(users.data.filter(u => u.organization_id === org.id));
    } catch {}
  };

  const handleCreateOrgAdmin = async () => {
    if (!selectedOrg) return;
    if (!adminForm.name.trim() || !adminForm.email.trim() || adminForm.password.length < 6) {
      toast.error('Completa nombre, email y contrasena (min 6 caracteres)');
      return;
    }
    setCreatingAdmin(true);
    try {
      const payload = {
        ...adminForm,
        role: 'admin',
        organization_id: selectedOrg.id,
        site_id: orgSites[0]?.id || undefined
      };
      await axios.post(`${API}/auth/users`, payload, { withCredentials: true });
      toast.success('Admin creado para la organizacion');
      setShowAdminModal(false);
      setAdminForm({ name: '', email: '', password: '' });
      await openOrgDetail(selectedOrg);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(a || 0);

  const MetricCard = ({ icon: Icon, label, value, color }) => (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${color}-500/20`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
        <div><p className="text-sm text-slate-400">{label}</p><p className="text-2xl font-bold text-white">{value}</p></div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button></Link>
            <div className="border-l border-slate-700 pl-4">
              <h1 className="text-lg font-bold text-white flex items-center gap-2"><Shield className="w-5 h-5 text-purple-400" />Super Admin</h1>
              <p className="text-sm text-slate-400">Panel de administracion global</p>
            </div>
          </div>
          <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        {/* Metrics */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
            <MetricCard icon={Building2} label="Organizaciones" value={metrics.active_orgs} color="blue" />
            <MetricCard icon={Users} label="Usuarios" value={metrics.total_users} color="emerald" />
            <MetricCard icon={MapPin} label="Sedes" value={metrics.total_sites} color="amber" />
            <MetricCard icon={Wifi} label="Conectores" value={`${metrics.online_connectors}/${metrics.total_connectors}`} color="cyan" />
            <MetricCard icon={Camera} label="Camaras" value={metrics.total_cameras} color="purple" />
            <MetricCard icon={Car} label="Adentro" value={metrics.vehicles_inside} color="red" />
          </div>
        )}

        <div className="flex gap-6">
          {/* Org list */}
          <div className={`${selectedOrg ? 'w-1/2' : 'w-full'} transition-all`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Organizaciones</h2>
              <Button onClick={() => { setEditingOrg(null); setOrgForm({ name: '', business_name: '', tax_id: '', plan: 'free' }); setShowOrgModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-org-btn">
                <Plus className="w-4 h-4 mr-2" />Nueva
              </Button>
            </div>
            <div className="space-y-2">
              {orgs.map(org => (
                <Card key={org.id} className={`bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ${selectedOrg?.id === org.id ? 'border-blue-500' : ''}`}
                  onClick={() => openOrgDetail(org)} data-testid={`org-${org.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white font-medium">{org.name}</p>
                        <p className="text-slate-500 text-xs">{org.business_name || 'Sin razon social'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">{org.plan}</Badge>
                      <Badge className={org.status === 'active' ? 'bg-emerald-600' : 'bg-red-600'}>{org.status === 'active' ? 'Activa' : 'Inactiva'}</Badge>
                      <span className="text-slate-500 text-xs hidden sm:inline">{org.users_count}u · {org.sites_count}s</span>
                      <Button size="sm" variant="ghost" onClick={(e) => openImpersonateModal(org, e)}
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 h-8 px-2"
                        title="Modo soporte (impersonar)"
                        data-testid={`impersonate-${org.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Org detail panel */}
          {selectedOrg && (
            <div className="w-1/2">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">{selectedOrg.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openImpersonateModal(selectedOrg)} className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20" title="Modo soporte (impersonar)" data-testid="impersonate-detail-btn"><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingOrg(selectedOrg); setOrgForm({ name: selectedOrg.name, business_name: selectedOrg.business_name, tax_id: selectedOrg.tax_id, plan: selectedOrg.plan }); setShowOrgModal(true); }} className="text-slate-400 hover:text-white"><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleOrgStatus(selectedOrg.id)} className="text-slate-400 hover:text-white"><Power className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedOrg(null)} className="text-slate-400 hover:text-white">x</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-slate-500">Plan</p><p className="text-white font-medium">{selectedOrg.plan}</p></div>
                    <div><p className="text-slate-500">Estado</p><Badge className={selectedOrg.status === 'active' ? 'bg-emerald-600' : 'bg-red-600'}>{selectedOrg.status}</Badge></div>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm font-medium mb-2">Sedes ({orgSites.length})</p>
                    {orgSites.map(s => (
                      <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-slate-700/50">
                        <MapPin className="w-3 h-3 text-amber-400" /><span className="text-white text-sm">{s.name}</span>
                        <span className="text-slate-500 text-xs">{s.address || ''} {s.city || ''}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-slate-400 text-sm font-medium">Usuarios ({orgUsers.length})</p>
                      <Button
                        size="sm"
                        onClick={() => { setAdminForm({ name: '', email: '', password: '' }); setShowAdminModal(true); }}
                        className="bg-blue-600 hover:bg-blue-700 h-8"
                        data-testid="create-org-admin-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />Crear admin
                      </Button>
                    </div>
                    {orgUsers.length === 0 && (
                      <p className="text-amber-300 text-xs mb-2">
                        Esta organizacion no tiene usuarios. Crea un admin para poder impersonar y configurar sedes.
                      </p>
                    )}
                    {orgUsers.map(u => (
                      <div key={u.id} className="flex items-center justify-between py-1.5 border-b border-slate-700/50">
                        <div className="flex items-center gap-2">
                          <Users className="w-3 h-3 text-blue-400" /><span className="text-white text-sm">{u.name}</span>
                          <span className="text-slate-500 text-xs">{u.email}</span>
                        </div>
                        <Badge className="text-xs bg-slate-700">{u.role}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <footer className="py-3 sm:py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive" className="h-6 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span><span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      {/* Org Modal */}
      <Dialog open={showOrgModal} onOpenChange={setShowOrgModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle>{editingOrg ? 'Editar Organizacion' : 'Nueva Organizacion'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Nombre</Label><Input value={orgForm.name} onChange={(e) => setOrgForm({...orgForm, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white" data-testid="org-name-input" /></div>
            <div><Label className="text-slate-300">Razon Social</Label><Input value={orgForm.business_name} onChange={(e) => setOrgForm({...orgForm, business_name: e.target.value})} className="bg-slate-700 border-slate-600 text-white" /></div>
            <div><Label className="text-slate-300">CUIT/Tax ID</Label><Input value={orgForm.tax_id} onChange={(e) => setOrgForm({...orgForm, tax_id: e.target.value})} className="bg-slate-700 border-slate-600 text-white" /></div>
            <div><Label className="text-slate-300">Plan</Label>
              <Select value={orgForm.plan} onValueChange={(v) => setOrgForm({...orgForm, plan: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="free">Free</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrgModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleSaveOrg} className="bg-blue-600 hover:bg-blue-700" data-testid="save-org-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create org admin modal */}
      <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Crear admin inicial — {selectedOrg?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-3 text-sm text-blue-200">
              El usuario quedara asociado a esta organizacion
              {orgSites[0] ? ` y a la sede "${orgSites[0].name}"` : ' (se creara "Sede Principal" si no hay sedes)'}.
            </div>
            <div>
              <Label className="text-slate-300">Nombre</Label>
              <Input
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="org-admin-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="org-admin-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Contrasena</Label>
              <Input
                type="password"
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="org-admin-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleCreateOrgAdmin} disabled={creatingAdmin} className="bg-blue-600 hover:bg-blue-700" data-testid="save-org-admin-btn">
              {creatingAdmin ? 'Creando...' : 'Crear admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonation Modal */}
      <Dialog open={showImpersonateModal} onOpenChange={setShowImpersonateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-amber-400" />Iniciar modo soporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 text-sm text-amber-200">
              Vas a ver el sistema como un admin de <strong>{impersonateTarget?.name}</strong>. El motivo queda registrado en el historial de logs.
            </div>
            <div>
              <Label className="text-slate-300">Motivo (obligatorio)</Label>
              <Input
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="Ej: Ticket #1234 - Problema con OCR"
                className="bg-slate-700 border-slate-600 text-white mt-1"
                data-testid="impersonate-reason-input"
                autoFocus
              />
              <p className="text-slate-500 text-xs mt-1">Minimo 3 caracteres. Ej: ticket, llamada, email del cliente.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImpersonateModal(false)} className="border-slate-600 text-slate-300" data-testid="impersonate-cancel-btn">Cancelar</Button>
            <Button onClick={confirmImpersonate} disabled={impersonating} className="bg-amber-600 hover:bg-amber-700" data-testid="impersonate-confirm-btn">
              <Eye className="w-4 h-4 mr-2" />{impersonating ? 'Iniciando...' : 'Iniciar modo soporte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
